/**
 * Riepilogo Magazzino — tipi, formatter e atomi presentazionali condivisi tra
 * il container (RiepilogoDashboard) e i 4 pannelli delle sotto-tab.
 *
 * L'analitica ricca proviene da `/pos/warehouse-analytics`; i KPI operativi e
 * la timeline da `/pos/logistics/summary`. Nessun dato mockato.
 */
import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { PRODUCT_LOGISTIC_STATUS_LABELS_IT } from '@easyflow/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';

// ---------------------------------------------------------------------------
// Tipi payload analytics (subset consumato dalla dashboard).
// ---------------------------------------------------------------------------
export interface AnalyticsKpi {
  stock_value_cmp: number;
  stock_value_sell: number;
  active_skus: number;
  out_of_stock: number;
  below_reorder: number;
  avg_rotation: number;
  avg_doh: number | null;
  incoming_value: number;
  outgoing_value: number;
  returns_value: number;
  dead_stock_value: number;
  shrinkage_pct: number;
  /** Task #4432 — perdite in transito (trasferimenti), separate dallo shrinkage. */
  transit_loss_qty: number;
  transit_loss_value: number | null;
}

export interface RotationRow {
  product_id: string;
  name: string;
  sku: string;
  rotation: number;
  qty_sold_90d: number;
  qty: number;
}

export interface StatusDistRow {
  status: string;
  value: number;
  qty: number;
  color: string;
  // Index signature richiesta da recharts <Pie data> (ChartDataInput).
  [key: string]: string | number;
}

export interface CategoryTreemapRow {
  category: string;
  value: number;
  qty: number;
}

export interface AgingBucketRow {
  bucket: string;
  qty: number;
  value: number;
}

export interface LowStockRow {
  product_id: string;
  name: string;
  sku: string;
  qty: number;
  reorder_point: number;
  min_stock_level: number;
}

export interface ValueTrendPoint {
  date: string;
  value: number;
}

export interface StockVsSalesPoint {
  date: string;
  load: number;
  sales: number;
}

export interface WarehouseComparisonRow {
  warehouse_id: string;
  warehouse_name: string;
  warehouse_code: string | null;
  stock_value_cmp: number;
  stock_value_sell: number;
  qty: number;
  active_skus: number;
}

export interface WarehouseAnalytics {
  period: { from: string; to: string; days: number };
  kpi: AnalyticsKpi;
  top_rotation: RotationRow[];
  slow_movers: RotationRow[];
  status_distribution: StatusDistRow[];
  category_treemap: CategoryTreemapRow[];
  warehouse_comparison: WarehouseComparisonRow[];
  value_trend: ValueTrendPoint[];
  movement_heatmap: number[][];
  aging_buckets: AgingBucketRow[];
  low_stock: LowStockRow[];
  stock_vs_sales: StockVsSalesPoint[];
}

// ---------------------------------------------------------------------------
// Tipi payload summary operativo.
// ---------------------------------------------------------------------------
export interface SummaryKpi {
  movimenti_oggi: number;
  carichi_in_attesa: number;
  trasferimenti_in_transito: number;
  sessioni_inventario_aperte: number;
  ddt_da_emettere: number;
  ddt_da_ricevere: number;
  ordini_fornitore_aperti: number;
}

export interface TimelineEvent {
  kind: 'movement' | 'purchase_order' | 'ddt';
  id: string;
  subtype: string | null;
  event_at: string;
  title: string;
  source: string | null;
  dest: string | null;
  qty: string | number | null;
  doc_number: string | null;
  status: string | null;
}

export interface SummaryData {
  kpi: SummaryKpi;
  timeline: TimelineEvent[];
}

// ---------------------------------------------------------------------------
// Formatter localizzati.
// ---------------------------------------------------------------------------
export function fmtMoney(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export function fmtMoneyPrecise(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n);
}

export function fmtNum(n: number | null | undefined, digits = 0): string {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('it-IT', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n);
}

export function fmtDay(s: string | null | undefined): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

// Etichette IT canoniche dei stati logistici (enum EN dal backend).
export const STATUS_LABELS: Record<string, string> = { ...PRODUCT_LOGISTIC_STATUS_LABELS_IT };

export const CHART_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#22C55E', '#06B6D4', '#EF4444', '#3B82F6'];

// ---------------------------------------------------------------------------
// Periodo analitico.
// ---------------------------------------------------------------------------
export type PeriodKey = '7d' | '30d' | '90d' | 'ytd';

export const PERIOD_PRESETS: ReadonlyArray<{ key: PeriodKey; label: string }> = [
  { key: '7d', label: '7gg' },
  { key: '30d', label: '30gg' },
  { key: '90d', label: '90gg' },
  { key: 'ytd', label: 'YTD' },
];

export function periodBounds(key: PeriodKey): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString();
  if (key === 'ytd') return { from: new Date(now.getFullYear(), 0, 1).toISOString(), to };
  const days = key === '7d' ? 7 : key === '90d' ? 90 : 30;
  return { from: new Date(now.getTime() - days * 86400000).toISOString(), to };
}

// ---------------------------------------------------------------------------
// Atomi presentazionali.
// ---------------------------------------------------------------------------
export type KpiTone = 'indigo' | 'violet' | 'emerald' | 'cyan' | 'amber' | 'rose' | 'blue' | 'orange';

const TONE_ICON_BG: Record<KpiTone, string> = {
  indigo: 'bg-indigo-500/15 text-indigo-500 dark:text-indigo-400',
  violet: 'bg-violet-500/15 text-violet-500 dark:text-violet-400',
  emerald: 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-400',
  cyan: 'bg-cyan-500/15 text-cyan-500 dark:text-cyan-400',
  amber: 'bg-amber-500/15 text-amber-500 dark:text-amber-400',
  rose: 'bg-rose-500/15 text-rose-500 dark:text-rose-400',
  blue: 'bg-blue-500/15 text-blue-500 dark:text-blue-400',
  orange: 'bg-orange-500/15 text-orange-500 dark:text-orange-400',
};

const TONE_CARD_BG: Record<KpiTone, string> = {
  indigo: 'bg-indigo-500/[0.06] hover:shadow-indigo-500/10',
  violet: 'bg-violet-500/[0.06] hover:shadow-violet-500/10',
  emerald: 'bg-emerald-500/[0.06] hover:shadow-emerald-500/10',
  cyan: 'bg-cyan-500/[0.06] hover:shadow-cyan-500/10',
  amber: 'bg-amber-500/[0.06] hover:shadow-amber-500/10',
  rose: 'bg-rose-500/[0.06] hover:shadow-rose-500/10',
  blue: 'bg-blue-500/[0.06] hover:shadow-blue-500/10',
  orange: 'bg-orange-500/[0.06] hover:shadow-orange-500/10',
};

export interface KpiCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  tone: KpiTone;
  sub?: string;
  index?: number;
  onClick?: () => void;
  loading?: boolean;
  /** Contenuto opzionale in alto a destra (es. badge di stato). */
  badge?: ReactNode;
  /** Contenuto opzionale sotto il sub (es. indicatori, breakdown, toggle). */
  footer?: ReactNode;
}

export function KpiCard({ icon: Icon, label, value, tone, sub, index = 0, onClick, loading, badge, footer }: KpiCardProps) {
  const cardClass = `text-left w-full rounded-xl border border-border backdrop-blur-xl p-4 transition-all duration-300 h-full hover:shadow-lg hover:border-foreground/20 ${TONE_CARD_BG[tone]} ${onClick ? 'cursor-pointer' : 'cursor-default'}`;
  const motionProps = {
    initial: { opacity: 0, y: 20, scale: 0.96 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.4, delay: index * 0.05, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] },
    whileHover: { scale: 1.03, y: -3 },
  };
  const content = (
    <>
      <div className={badge ? 'flex items-start justify-between gap-2 mb-2' : 'mb-2'}>
        <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${TONE_ICON_BG[tone]}`}>
          <Icon className="h-4.5 w-4.5" />
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
      </div>
      <div className="text-2xl font-bold text-foreground tracking-tight tabular-nums">
        {loading ? <span className="inline-block h-7 w-16 rounded bg-foreground/10 animate-pulse align-middle" /> : value}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 truncate">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">{sub}</p>}
      {footer && <div className="mt-2">{footer}</div>}
    </>
  );
  if (onClick) {
    return (
      <motion.button type="button" onClick={onClick} {...motionProps} className={cardClass}>
        {content}
      </motion.button>
    );
  }
  return (
    <motion.div {...motionProps} className={cardClass}>
      {content}
    </motion.div>
  );
}

export interface ChartCardProps {
  title: string;
  icon: LucideIcon;
  subtitle?: string;
  className?: string;
  action?: ReactNode;
  children: ReactNode;
}

export function ChartCard({ title, icon: Icon, subtitle, className = '', action, children }: ChartCardProps) {
  return (
    <Card className={`border-border bg-card ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 text-indigo-500 flex-shrink-0" />
            <span className="truncate">{title}</span>
          </span>
          {action ?? (subtitle && <span className="text-[11px] font-normal text-muted-foreground flex-shrink-0">{subtitle}</span>)}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {children}
      </CardContent>
    </Card>
  );
}

export function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground text-center py-10">{message}</p>;
}

/**
 * Etichetta di sezione: crea gerarchia e ordine di lettura raggruppando i
 * widget correlati. Usata dai pannelli delle sotto-tab per dare struttura.
 */
export function SectionLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="flex items-center gap-3 pt-1">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">
        {children}
      </h3>
      {hint && <span className="text-[11px] text-muted-foreground/60 whitespace-nowrap">{hint}</span>}
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function MovementHeatmap({ matrix }: { matrix: number[][] }) {
  const flat = matrix.flat();
  const max = Math.max(1, ...flat);
  const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  return (
    <div className="overflow-x-auto">
      <table className="text-[10px]">
        <tbody>
          {matrix.map((row, di) => (
            <tr key={di}>
              <td className="pr-1 text-muted-foreground font-medium w-8">{days[di]}</td>
              {row.map((v, hi) => {
                const intensity = v / max;
                const bg = v === 0 ? 'rgba(148, 163, 184, 0.15)' : `rgba(99, 102, 241, ${0.15 + intensity * 0.85})`;
                return (
                  <td key={hi} className="p-0">
                    <div title={`${days[di]} ${hi}:00 — ${v} mov.`} style={{ background: bg }} className="w-3.5 h-3.5 m-px rounded-sm" />
                  </td>
                );
              })}
            </tr>
          ))}
          <tr>
            <td />
            {Array.from({ length: 24 }).map((_, h) => (
              <td key={h} className="text-center text-[8px] text-muted-foreground/70">
                {h % 3 === 0 ? h : ''}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
